import ApiClient from '@afosto/api/ApiClient';

class Session {
  constructor(client) {
    this.apiClient = client || ApiClient;
  }

  /**
   * Attach an order to the session
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Lets an user authenticate their contact through username and password
   * @returns {Promise}
   */
  attachSession(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/order`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Authenticate a contact for the session
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Lets an user authenticate their contact through username and password
   * @returns {Promise}
   */
  authenticateSession(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/authenticate`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Authorize the session calculation
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description authorize session and continue to payment
   * @returns {Promise}
   */
  authorizeSession(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/authorize`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Create address
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create a address
   * @returns {Promise}
   */
  createSessionAddress(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/address`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Add a new contact to the session
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description create a new contact and add it to the session
   * @returns {Promise}
   */
  createSessionContact(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/contact`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Create organisation
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description create an organisation
   * @returns {Promise}
   */
  createSessionOrganisation(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/organisations`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Add a contact phonenumber to the session
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Create a phonenumber
   * @returns {Promise}
   */
  createSessionPhoneNumber(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/phonenumbers`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Returns the session expiry date
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns the session expiry date
   * @returns {Promise}
   */
  getSession(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get billing address
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a list of applicable shipping methods
   * @returns {Promise}
   */
  getSessionBilling(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/billing`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get billing method
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a list of known shipping addresses
   * @returns {Promise}
   */
  getSessionBillingMethod(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/billing/methods`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get the current contact information
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns the contact information
   * @returns {Promise}
   */
  getSessionContact(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/contact`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get organisation
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description get the current organisation information
   * @returns {Promise}
   */
  getSessionOrganisation(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/organisations`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get the contact phonenumber
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns the primary phonenumber of the customer
   * @returns {Promise}
   */
  getSessionPhonenumber(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/phonenumbers`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get the order reference
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description get the order reference
   * @returns {Promise}
   */
  getSessionReference(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/reference`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get a calculation for the order
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a calculation for the session
   * @returns {Promise}
   */
  getSessionSessionCalculation(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/calculation`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get the price projection for the order
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a projections for the session
   * @returns {Promise}
   */
  getSessionSessionProjection(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/projection`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get shipping method
   *
   * @param {string} id
   * @param {Object} query - The optional path parameters
   * @param {string} [query.countryCode]
   * @param {Object} options - The additional request options
   * @description Returns a list of known shipping addresses
   * @returns {Promise}
   */
  getSessionShippingMethod(id, query = {}, options = {}) {
    const params = {
      country_code: query.countryCode,
    };

    return this.apiClient.request({
      url: `/odr/sessions/${id}/shipping/methods`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get shipping point
   *
   * @param {string} id
   * @param {Object} query - The optional path parameters
   * @param {string} [query.countryCode] - the country used to select shipping points
   * @param {string} [query.methodId] - method_id
   * @param {string} [query.postalCode] - prefiled with the postal_code of the primary address
   * @param {Object} options - The additional request options
   * @description Returns a list of known shipping addresses
   * @returns {Promise}
   */
  getSessionShippingPoint(id, query = {}, options = {}) {
    const params = {
      country_code: query.countryCode,
      method_id: query.methodId,
      postal_code: query.postalCode,
    };

    return this.apiClient.request({
      url: `/odr/sessions/${id}/shipping/point`,
      method: 'get',
      params,
      ...options,
    });
  }

  /**
   * Get shipping window
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description Returns a list of known shipping addresses
   * @returns {Promise}
   */
  getSessionShippingWindow(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/shipping/window`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Get shipping address
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description RReturns the shipping address and available options
   * @returns {Promise}
   */
  getSessionShipppingAddress(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/shipping`,
      method: 'get',
      ...options,
    });
  }

  /**
   * Remove active organisaton
   *
   * @param {string} id
   * @param {Object} options - The additional request options
   * @description removes the current selected organisation from the session
   * @returns {Promise}
   */
  removeSessionOrganisation(id, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/organisations`,
      method: 'delete',
      ...options,
    });
  }

  /**
   * Set billing method
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Select billing method
   * @returns {Promise}
   */
  selectSessionBillingMethodRequest(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/billing/methods`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Set shipping address
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Select a billing address
   * @returns {Promise}
   */
  selectSessionBillingRequest(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/shipping`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Set organisation
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Select an organisation
   * @returns {Promise}
   */
  selectSessionOrganisationRequest(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/organisations`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Select a phonenumber
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description select a phonenumber
   * @returns {Promise}
   */
  selectSessionPhonenumber(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/phonenumbers`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Set shipping method
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description select the used shipping method
   * @returns {Promise}
   */
  selectSessionShippingMethod(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/shipping/methods`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Set billing address
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description set the shipping address used for the session
   * @returns {Promise}
   */
  selectSessionShippingOption(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/billing`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Set shipping point
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description select the used shipping method
   * @returns {Promise}
   */
  selectSessionShippingPoint(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/shipping/point`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Set shipping window
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description select the used shipping window
   * @returns {Promise}
   */
  selectSessionShippingWindow(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/shipping/window`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Validate the existence of an identity
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description Returns a 204 when the identity exists
   * @returns {Promise}
   */
  sessionIdentityExists(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/identity`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Authenticate a contact for the session
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description set an authenticated user on a session
   * @returns {Promise}
   */
  setAuthenticatedUser(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/authenticated`,
      method: 'post',
      data: body,
      ...options,
    });
  }

  /**
   * Set an reference on the order
   *
   * @param {string} id
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description set an reference on the order
   * @returns {Promise}
   */
  setSessionReference(id, body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions/${id}/reference`,
      method: 'put',
      data: body,
      ...options,
    });
  }

  /**
   * Start a checkout session
   *
   * @param {Object} body
   * @param {Object} options - The additional request options
   * @description start a checkout session
   * @returns {Promise}
   */
  startSession(body, options = {}) {
    return this.apiClient.request({
      url: `/odr/sessions`,
      method: 'post',
      data: body,
      ...options,
    });
  }
}

export const session = new Session();

export default Session;
